/**
 * The overlay that shows what the app is reporting, as it reports it.
 *
 * Styles are inline and absolute rather than themed. The panel sits on top of a page
 * whose colours come from the creditor, and a debug tool that changes appearance per
 * creditor -- or worse, inherits a white-on-white combination from one -- is a tool people
 * stop trusting. It also deliberately does not use react-jss, so it renders identically
 * whether or not the app's own ThemeProvider has mounted.
 */
import {useEffect, useState} from 'react';

import {clear, getEntries, subscribe} from './eventLog';

const COLOURS = {
    bg: '#12141a',
    border: '#2b2f3a',
    dim: '#7a8194',
    failed: '#ff6b6b',
    mixpanel: '#c792ea',
    ok: '#6ee7a8',
    server: '#7fd1ff',
    text: '#e6e9ef'
};

const styles = {
    badge: {
        background: COLOURS.bg,
        border: `1px solid ${COLOURS.border}`,
        borderRadius: 6,
        bottom: 12,
        color: COLOURS.text,
        cursor: 'pointer',
        font: '12px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace',
        padding: '6px 10px',
        position: 'fixed',
        right: 12,
        zIndex: 2147483647
    },
    body: {flex: 1, overflowY: 'auto'},
    button: {
        background: 'transparent',
        border: `1px solid ${COLOURS.border}`,
        borderRadius: 4,
        color: COLOURS.dim,
        cursor: 'pointer',
        font: 'inherit',
        marginLeft: 6,
        padding: '2px 8px'
    },
    head: {
        alignItems: 'center',
        borderBottom: `1px solid ${COLOURS.border}`,
        display: 'flex',
        gap: 6,
        padding: '8px 10px'
    },
    json: {
        color: COLOURS.dim,
        margin: '6px 0 0',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    },
    root: {
        background: COLOURS.bg,
        border: `1px solid ${COLOURS.border}`,
        borderRadius: 8,
        bottom: 12,
        color: COLOURS.text,
        display: 'flex',
        flexDirection: 'column',
        font: '12px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace',
        maxHeight: '60vh',
        position: 'fixed',
        right: 12,
        width: 'min(520px, calc(100vw - 24px))',
        zIndex: 2147483647
    },
    row: {borderBottom: `1px solid ${COLOURS.border}`, cursor: 'pointer', padding: '6px 10px'},
    spacer: {flex: 1}
};

const railStyle = rail => ({
    color: rail === 'mixpanel' ? COLOURS.mixpanel : COLOURS.server,
    fontWeight: 700
});

const time = at => at.toTimeString().slice(0, 8);

const Row = ({entry}) => {
    const [open, setOpen] = useState(false);
    const {data, failed, method, ms, name, rail, status} = entry;
    const hasData = data && Object.keys(data).length > 0;

    return (
        <div onClick={() => setOpen(!open)} style={styles.row}>
            <span style={{color: COLOURS.dim}}>{time(entry.at)} </span>
            <span style={railStyle(rail)}>{rail === 'mixpanel' ? 'MP' : method || 'SL'} </span>
            <span>{name}</span>
            {status !== undefined && (
                <span style={{color: failed ? COLOURS.failed : COLOURS.ok}}> {status}</span>
            )}
            {ms !== undefined && <span style={{color: COLOURS.dim}}> {ms}ms</span>}
            {hasData && <span style={{color: COLOURS.dim}}> {open ? '▾' : '▸'}</span>}
            {open && hasData && <pre style={styles.json}>{JSON.stringify(data, null, 2)}</pre>}
        </div>
    );
};

const DebugPanel = () => {
    const [entries, setEntries] = useState(getEntries());
    const [open, setOpen] = useState(false);
    const [rail, setRail] = useState('all');

    useEffect(() => subscribe(setEntries), []);

    const shown = rail === 'all' ? entries : entries.filter(entry => entry.rail === rail);

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} style={styles.badge} type="button">
                events {entries.length}
            </button>
        );
    }

    return (
        <div style={styles.root}>
            <div style={styles.head}>
                <strong>events</strong>
                <span style={{color: COLOURS.dim}}>{shown.length}</span>
                <span style={styles.spacer} />
                {['all', 'mixpanel', 'server'].map(option => (
                    <button
                        key={option}
                        onClick={() => setRail(option)}
                        style={{
                            ...styles.button,
                            ...rail === option && {borderColor: COLOURS.text, color: COLOURS.text}
                        }}
                        type="button">
                        {option}
                    </button>
                ))}
                <button onClick={clear} style={styles.button} type="button">clear</button>
                <button onClick={() => setOpen(false)} style={styles.button} type="button">×</button>
            </div>
            <div style={styles.body}>
                {shown.length === 0 && (
                    <div style={{color: COLOURS.dim, padding: 10}}>
                        Nothing reported yet. Interact with the page.
                    </div>
                )}
                {shown.map(entry => <Row entry={entry} key={entry.id} />)}
            </div>
            <div style={{...styles.head, borderBottom: 'none', borderTop: `1px solid ${COLOURS.border}`}}>
                <span style={{color: COLOURS.dim}}>
                    Sensitive values are masked to a length before being stored.
                </span>
            </div>
        </div>
    );
};

export default DebugPanel;
